import { RefObject, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

interface DOMRectReadOnly {
  readonly bottom: number;
  readonly height: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly width: number;
  readonly x: number;
  readonly y: number;
}

/**
 * useResizeObserver
 *
 * A hook that provides a ref and the current dimensions of whatever
 * component the ref is passed into. This is useful when you want to
 * be notified any time a resize occurs, not just during the
 * initial mount and unmount.
 *
 * General documentation: https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *
 * Code adapted from: https://github.com/asyarb/use-resize-observer/blob/master/src/index.tsx and
 * https://github.com/antonioru/beautiful-react-hooks/blob/master/src/useResizeObserver.ts
 */
export const useResizeObserver = <T extends HTMLElement>(
  elementRef: RefObject<T>,
  debounceTimeout = 100,
) => {
  const observerRef = useRef<ResizeObserver | null>(null);

  const [sizes, setSizes] = useState<DOMRectReadOnly>({
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const fn = debounce((entries) => {
      const [entry] = entries;

      setSizes(entry.contentRect);
    }, debounceTimeout);

    observerRef.current = new ResizeObserver(fn);

    return () => {
      fn.cancel();
      observerRef.current?.disconnect();
    };
  }, []);

  // observes on the provided element ref
  useEffect(() => {
    if (elementRef.current) {
      observerRef.current?.observe(elementRef.current);
    }
  }, [elementRef.current]);

  return sizes;
};
